import { createClient } from '@supabase/supabase-js';
import { logApiCall, logInfo,logError, startTimer } from './logger';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL || '';
const supabaseKey = process.env.REACT_APP_SUPABASE_ANON_KEY || '';

export const supabase = createClient(supabaseUrl, supabaseKey);

// Define types for our database tables
interface lcaData {
    id: number;
    inserted_at: string;
    updated_at: string;
    case_status: string;
    received_date: string;
    decision_date: string;
    job_title: string;
    soc_title: string;
    full_time_position: boolean;
    employer_name: string;
    employer_city: string;
    prevailing_wage: number;
    pw_unit_of_pay: string;
    pw_wage_level: string;
    worksite_city: string;
    wage_rate_of_pay_from: number;
    wage_rate_of_pay_to: number;
    wage_unit_of_pay: string;
}

// Cache for autocomplete data to avoid repeated database calls
const autocompleteCache = new Map<string, { data: string[], timestamp: number }>();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

// Helper function to check if cache is valid
const isCacheValid = (timestamp: number): boolean => {
  return Date.now() - timestamp < CACHE_DURATION;
};

// Optimized function to search company names with server-side filtering
export async function searchCompanyNames(searchTerm: string, limit: number = 10): Promise<string[]> {
  if (!searchTerm || searchTerm.length < 2) {
    return [];
  }

  const cacheKey = `companies_${searchTerm.toLowerCase()}_${limit}`;
  const cached = autocompleteCache.get(cacheKey);
  
  if (cached && isCacheValid(cached.timestamp)) {
    logInfo("Returned company names from cache");
    return cached.data;
  }

  const endTimer = startTimer('supabase_searchCompanyNames');
  try {
    const { data, error } = await supabase
      .from('lca_data')
      .select('employer_name')
      .ilike('employer_name', `${searchTerm}%`)
      .not('employer_name', 'is', null)
      .limit(limit * 3) // Get more to account for duplicates
      .order('employer_name');

    const duration = endTimer();

    if (error) {
      console.error('Error searching company names:', error);
      logError(error, { function: 'searchCompanyNames', searchTerm, limit });
      logApiCall('searchCompanyNames', 'GET', duration, 400, { 
        searchTerm, 
        limit, 
        error: true 
      });
      return [];
    }

    // Deduplicate and limit results
    const uniqueCompanies = Array.from(
      new Set(data?.map(item => item.employer_name?.trim()).filter(Boolean))
    ).slice(0, limit);

    // Cache the results
    autocompleteCache.set(cacheKey, {
      data: uniqueCompanies,
      timestamp: Date.now()
    });

    logApiCall('searchCompanyNames', 'GET', duration, 200, { 
      searchTerm, 
      limit, 
      resultCount: uniqueCompanies.length,
      fromCache: false 
    });

    return uniqueCompanies;
  } catch (err) {
    const duration = endTimer();
    console.error('Unexpected error searching company names:', err);
    logError(err as Error, { function: 'searchCompanyNames', searchTerm, limit });
    logApiCall('searchCompanyNames', 'GET', duration, 500, { 
      searchTerm, 
      limit, 
      error: true 
    });
    return [];
  }
}

// Optimized function to search job titles with server-side filtering
export async function searchJobTitles(searchTerm: string, limit: number = 10): Promise<string[]> {
  if (!searchTerm || searchTerm.length < 2) {
    return [];
  }

  const cacheKey = `titles_${searchTerm.toLowerCase()}_${limit}`;
  const cached = autocompleteCache.get(cacheKey);
  
  if (cached && isCacheValid(cached.timestamp)) {
    logInfo("Returned data from cache");
    return cached.data;
  }

  try {
    const { data, error } = await supabase
      .from('lca_data')
      .select('job_title')
      .ilike('job_title', `${searchTerm}%`)
      .not('job_title', 'is', null)
      .limit(limit) // Get more to account for duplicates
      .order('job_title');

    if (error) {
      console.error('Error searching job titles:', error);
      return [];
    }

    // Deduplicate, format, and limit results
    const uniqueTitles = Array.from(
      new Set(
        data?.map(item => {
          const title = item.job_title?.trim();
          if (!title) return null;
          // Format title with proper capitalization
          return title.toLowerCase()
            .split(' ')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
        }).filter(Boolean)
      )
    ).slice(0, limit);

    // Cache the results
    autocompleteCache.set(cacheKey, {
      data: uniqueTitles,
      timestamp: Date.now()
    });

    return uniqueTitles;
  } catch (err) {
    console.error('Unexpected error searching job titles:', err);
    return [];
  }
}

// Function to clear autocomplete cache (useful for testing or manual cache invalidation)
export function clearAutocompleteCache(): void {
  autocompleteCache.clear();
}

export async function fetchJobsFromRemote({
  limit = 100,
  cursor = null,
  cursorId = null,
  employerName = '',
  position = null,
  city = null,
  year = null,
}: {
  limit?: number;
  cursor?: number | null; // cursor is the wage_rate_of_pay_from value of the last item
  cursorId?: number | null; // cursorId is the id of the last item for tie-breaking
  employerName?: string | null;
  position?: string | null;
  city?: string | null;
  year?: number | null;
}) {  
  let query = supabase
    .from('lca_data')
    .select('*')
    .order('wage_rate_of_pay_from', { ascending: false })
    .order('id', { ascending: false }) // Secondary sort for consistent pagination
    .limit(limit);

  // Apply cursor-based pagination
  if (cursor !== null) {
    if (cursorId !== null) {
      // Use compound cursor for more precise pagination
      const cursorCondition = `wage_rate_of_pay_from.lt.${cursor},and(wage_rate_of_pay_from.eq.${cursor},id.lt.${cursorId})`;
      console.log('Using compound cursor condition:', cursorCondition);
      query = query.or(cursorCondition);
    } else {
      // Fallback to simple cursor
      console.log('Using simple cursor condition: wage_rate_of_pay_from <', cursor);
      query = query.lt('wage_rate_of_pay_from', cursor);
    }
  }

  if (employerName) {
    query = query.eq(
      'employer_name', employerName);
  }

  if (position) {
    query = query.ilike('job_title', "*"+position+"*");
  }

  if (city) {
    query = query.ilike('worksite_city', "*"+city+"*");
  }

  if (year) {
    query = query.gte('decision_date',`${year}-01-01`).lte('decision_date',`${year}-12-31`);
  }

  const { data, error } = await query;

  if (error) {
    console.error('Error fetching jobs:', error);
    throw error;
  }

  //console.log('fetchJobsFromRemote returned:', data?.length, 'jobs');
  if (data && data.length > 0) {
    console.log('First job wage:', data[0].wage_rate_of_pay_from, 'Last job wage:', data[data.length - 1].wage_rate_of_pay_from);
  }

  return data as lcaData[] || [];
} 